import { isConsultant, isRestrictedUser } from "../../../../utils/PermissionUtils";
import Icon from "../../../common/Icon";

const Summary = ({ displayData, handleAddContact, selectedClient, renderContactCards, loadingContactDetails, clientDetails, handleInputChange, fieldPerms }) => {

    // ✅ RBAC helper: missing prop / missing key = visible (internal users).
    // Only an explicit `false` hides a section.
    const can = (key) => fieldPerms?.[key] !== false;

    // Internal cost/net/ROI metrics are nulled by ClientDashboardMain when blocked
    const canViewCostMetrics =
        displayData.grossCost !== null &&
        displayData.netRevenue !== null &&
        displayData.totalROI !== null;

    // ✅ RBAC: Client and Consultant must not see Contacts on the Summary tab —
    // Postal Information takes its place in the right column instead (see below).
    const isRestricted = isRestrictedUser();
    const canViewContacts = !isRestricted;

    // Hide the whole Accounting Details card when every row in it is blocked
    const canViewAccountingSection =
        can('creativeFee') || can('designFee') || can('accountingType') ||
        can('sageClientCode') || can('salesCommission') || can('contractDate');

    // ✅ Null-safe formatters (nulls come from RBAC stripping OR missing API data)
    const money = (v) => (v ?? 0).toLocaleString();
    const pct = (v) => Math.abs(v ?? 0).toFixed(2);

    // ✅ Shared Postal Information markup — rendered either in Row 1 (in place
    // of Contacts, for Client/Consultant) or in Row 2 (for everyone else).
    const postalInformationBlock = (
        <div className="contact-section-tab-1">
            <div className="postal-information-tab">
                <h2 className="client-section-subtitle">Postal Information</h2>

                <div className="postal-row">
                    <div className='input-group-postal'>
                        <label htmlFor="cagingPOBox">
                            <Icon name="location" size={16} alt="location-icon" className="me-2" />
                            Caging PO Box Address
                        </label>
                        <input
                            type="text"
                            id="cagingPOBox"
                            name="cagingPOBox"
                            value={clientDetails?.cagingPOBoxNumber ? clientDetails?.cagingPOBoxNumber : ''}
                            onChange={handleInputChange}
                            placeholder="Not available"
                            readOnly
                        />
                    </div>

                    <div className="postal-gap"></div>

                    <div className='input-group-postal'>
                        <label htmlFor="brePOBox">
                            <Icon name="location" size={16} alt="location-icon" className="me-2" />
                            BRE PO Box Address
                        </label>
                        <input
                            type="text"
                            id="brePOBox"
                            name="brePOBox"
                            value={clientDetails?.brepoBoxNumber ? clientDetails?.brepoBoxNumber : ''}
                            onChange={handleInputChange}
                            placeholder="Not available"
                            readOnly
                        />
                    </div>
                </div>

                <div className='postal-row'>
                    <div className='input-group-postal'>
                        <label htmlFor="uspsAuthCode">
                            <Icon name="postal" size={16} alt="postal-icon" className="me-2" />
                            USPS NP Authorization Code
                        </label>
                        <input
                            type="text"
                            id="uspsAuthCode"
                            name="uspsAuthCode"
                            value={clientDetails?.npAuthorizationNumber ? clientDetails?.npAuthorizationNumber : ''}
                            onChange={handleInputChange}
                            placeholder="Not available"
                            readOnly
                        />
                    </div>

                    <div className="postal-gap"></div>

                    <div className='input-group-postal'>
                        <label htmlFor="uspsCRID">
                            <Icon name="postal" size={16} alt="postal-icon" className="me-2" />
                            USPS CRID
                        </label>
                        <input
                            type="text"
                            id="uspsCRID"
                            name="uspsCRID"
                            value={clientDetails?.crid ? clientDetails?.crid : ''}
                            onChange={handleInputChange}
                            placeholder="Not available"
                            readOnly
                        />
                    </div>
                </div>

                <div className='postal-row'>
                    <div className='input-group-postal'>
                        <label htmlFor="uspsMID">
                            <Icon name="postal" size={16} alt="postal-icon" className="me-2" />
                            USPS MID
                        </label>
                        <input
                            type="text"
                            id="uspsMID"
                            name="uspsMID"
                            value={clientDetails?.mid ? clientDetails?.mid : ''}
                            onChange={handleInputChange}
                            placeholder="Not available"
                            readOnly
                        />
                    </div>

                    <div className="postal-gap"></div>

                    <div className='input-group-postal'>
                        <label htmlFor="uspsPermit">
                            <Icon name="postal" size={16} alt="postal-icon" className="me-2" />
                            USPS Permit Number
                        </label>
                        <input
                            type="text"
                            id="uspsPermit"
                            name="uspsPermit"
                            value={clientDetails?.permitNumber ? clientDetails?.permitNumber : ''}
                            onChange={handleInputChange}
                            placeholder="Not available"
                            readOnly
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="summary-section">
            <div className="summary-content">

                {/* ===== ROW 1: Internal Roles  ↔  (Metrics + Contacts/Postal) ===== */}
                <div className="summary-row">
                    {/* Left: Internal Roles — ✅ RBAC: internalRolesPanel */}
                    {can('internalRolesPanel') && (
                        <div className="summary-col-left">
                            <div className="internal-roles-section">
                                <h5 className="client-section-subtitle">Internal Roles</h5>
                                <div className="roles-list">
                                    <div className="role-item">
                                        <span className="role-title">Account Manager</span>
                                        <strong className="role-person">{displayData.accountManager || "Not assigned"}</strong>
                                    </div>
                                    <div className="role-item">
                                        <span className="role-title">Production Manager</span>
                                        <strong className="role-person">{displayData.productionManager || "Not assigned"}</strong>
                                    </div>
                                    <div className="role-item">
                                        <span className="role-title">List Manager</span>
                                        <strong className="role-person">{displayData.listManager || "Not assigned"}</strong>
                                    </div>
                                    <div className="role-item">
                                        <span className="role-title">Consultant</span>
                                        <strong className="role-person">{displayData.consultant || "Not assigned"}</strong>
                                    </div>
                                    <div className="role-item">
                                        <span className="role-title">Creative Director</span>
                                        <strong className="role-person">{displayData.creativeDirector || "Not assigned"}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Right: Metrics + (Contacts OR Postal Information) */}
                    <div className="summary-col-right">
                        {/* Metrics Widgets Section */}
                        <div className="">
                            <div className="metrics-widgets-grid">
                                {/* Gross Revenue — income-side, visible to clients */}
                                <div className="metrics-widget-card">
                                    <div className="widget-header">
                                        <h3 className="widget-title">Gross Revenue for 2026</h3>
                                    </div>
                                    <div className="widget-value">
                                        ${money(displayData.grossRevenue)}
                                    </div>
                                    <div className={`widget-trend ${(displayData.grossRevenuePL ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                                        {(displayData.grossRevenuePL ?? 0) >= 0 ? '↑' : '↓'} {pct(displayData.grossRevenuePL)}% vs last year
                                    </div>
                                </div>

                                {/* ✅ RBAC: Cost / Net / ROI widgets are internal-only */}
                                {/* {canViewCostMetrics && ( */}
                                <>
                                    <div className="metrics-widget-card">
                                        <div className="widget-header">
                                            <h3 className="widget-title">Gross Cost for 2026</h3>
                                        </div>
                                        <div className="widget-value">
                                            ${money(displayData.grossCost)}
                                        </div>
                                        <div className={`widget-trend ${(displayData.grossCostPL ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                                            {(displayData.grossCostPL ?? 0) >= 0 ? '↑' : '↓'} {pct(displayData.grossCostPL)}% vs last year
                                        </div>
                                    </div>

                                    <div className="metrics-widget-card">
                                        <div className="widget-header">
                                            <h3 className="widget-title">Net Revenue for 2026</h3>
                                        </div>
                                        <div className="widget-value">
                                            ${money(displayData.netRevenue)}
                                        </div>
                                        <div className={`widget-trend ${(displayData.netRevenuePL ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                                            {(displayData.netRevenuePL ?? 0) >= 0 ? '↑' : '↓'} {pct(displayData.netRevenuePL)}% vs last year
                                        </div>
                                    </div>

                                    <div className="metrics-widget-card">
                                        <div className="widget-header">
                                            <h3 className="widget-title">Total ROI for 2026</h3>
                                        </div>
                                        <div className="widget-value">
                                            {(displayData.totalROI ?? 0).toFixed(2)}%
                                        </div>
                                        <div className={`widget-trend ${(displayData.lastTotalROI ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                                            {(displayData.lastTotalROI ?? 0) >= 0 ? '↑' : '↓'} {pct(displayData.lastTotalROI)}% vs last year
                                        </div>
                                    </div>
                                </>
                                {/* )} */}
                            </div>
                        </div>

                        {/* ✅ RBAC: Client/Consultant see Postal Information here instead of Contacts */}
                        {isRestricted ? (
                            can('postalInformation') && postalInformationBlock
                        ) : (
                            <div className="contact-section-tab-1">
                                <div className="add-contact-section">
                                    <div className="contact-header-fixed d-flex justify-content-between align-items-center pb-4">
                                        <h2 className="client-section-subtitle">Contacts</h2>
                                        <button
                                            className="add-contact-btn"
                                            aria-label="Add new contact"
                                            onClick={handleAddContact}
                                            disabled={!selectedClient}
                                        >
                                            <span className="me-2">+</span>Add Contact
                                        </button>
                                    </div>

                                    {renderContactCards()}

                                    {loadingContactDetails && (
                                        <div className="alert alert-info mt-3 p-2" role="alert">
                                            <small>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Fetching contact details...
                                            </small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ===== ROW 2: Accounting Details  ↔  Postal Information ===== */}
                <div className="summary-row">
                    {/* Left: Accounting Details — ✅ RBAC: hidden entirely for clients */}
                    {canViewAccountingSection && (
                        <div className="summary-col-left">
                            <div className="contact-info-section">
                                <h5 className="client-section-subtitle">Accounting Details</h5>
                                <div className="accounting-details-list">
                                    {can('creativeFee') && (
                                        <div className="contact-combined-row">
                                            <span className="contact-label">Creative Fee</span>
                                            <div className="contact-input-container">
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    value={`$${displayData.creativeFee?.toLocaleString() || "0"}/M`}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {can('designFee') && (
                                        <div className="contact-row">
                                            <span className="contact-label">Design Fee</span>
                                            <div className="contact-input-container">
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    value={`$${displayData.designFee?.toLocaleString() || "0"}/M`}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {can('accountingType') && (
                                        <div className="contact-row">
                                            <span className="contact-label">Account Type</span>
                                            <div className="contact-input-container">
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    value={displayData.acctType || "Not specified"}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {can('sageClientCode') && (
                                        <div className="contact-row">
                                            <span className="contact-label">Sage Client Code</span>
                                            <div className="contact-input-container">
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    value={displayData.sageClientCode || "Not assigned"}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {can('salesCommission') && (
                                        <div className="contact-row">
                                            <span className="contact-label">Sales Commission</span>
                                            <div className="contact-input-container">
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    value={`${displayData.salesCommission || "0"}%`}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {can('contractDate') && (
                                        <div className="contact-row">
                                            <span className="contact-label">Contract Date</span>
                                            <input
                                                type="text"
                                                className="contact-input"
                                                value={
                                                    displayData.contractDate
                                                        ? (() => {

                                                            const datePart = displayData.contractDate.split('T')[0];
                                                            const [year, month, day] = datePart.split('-');
                                                            return `${month}/${day}/${year}`;
                                                        })()
                                                        : "Not specified"
                                                }
                                                readOnly
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Right: Postal Information — ✅ RBAC: postalInformation.
                        Client/Consultant already see this in Row 1's right column
                        instead of Contacts, so skip it here to avoid duplication. */}
                    {!isRestricted && can('postalInformation') && (
                        <div className="summary-col-right" style={{ width: "100% !important" }}>
                            {postalInformationBlock}
                        </div>
                    )}
                </div>

                {/* ===== CONTRACT INFORMATION — ✅ RBAC: internal terms, hidden for clients ===== */}
                {can('contractDate') && (
                    <div className="contract-info-full-width">
                        <div className="contact-info-section contract-info-box">
                            <h5 className="client-section-subtitle">Contract Information</h5>
                            <div className="accounting-details-list">
                                <textarea
                                    disabled
                                    className="contract-info-textarea"
                                    value={clientDetails?.clientContractInfo ? clientDetails?.clientContractInfo : ""}
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
export default Summary;